import { ReactElement, useState } from "react";
import { ManageSecondmentEditor, ManageSecondmentOption } from "../../../Services/ManageSecondments";

const DURATIONS = [
  { id: 1, name: "Open" },
  { id: 2, name: "3 months" },
  { id: 3, name: "6 months" },
  { id: 4, name: "1 year" },
  { id: 5, name: "More than 1 year" },
];

interface ScheduleProps {
  editor: ManageSecondmentEditor;
  contactId: number;
  openDeadline: boolean;
  deadline: string;
  openPosting: boolean;
  dateFrom: string;
  dateTo: string;
  title: string;
  duration: number;
  onContact: (value: number) => void;
  onOpenDeadline: (value: boolean) => void;
  onDeadline: (value: string) => void;
  onOpenPosting: (value: boolean) => void;
  onDateFrom: (value: string) => void;
  onDateTo: (value: string) => void;
  onTitle: (value: string) => void;
  onDuration: (value: number) => void;
}

export function SecondmentSchedule(props: ScheduleProps): ReactElement {
  return (
    <div className="stack">
      <label className="inputLabel">Contact Name *</label>
      <select className="border border-gray-300 p-2" value={props.contactId || ""} onChange={(event) => props.onContact(Number(event.target.value))}>
        <option value="" />
        {props.editor.contacts.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
      </select>
      <label className="inputLabel">Application Deadline *</label>
      <label className="caption"><input type="checkbox" checked={props.openDeadline} onChange={(event) => props.onOpenDeadline(event.target.checked)} /> Open / No Deadline</label>
      <input type="date" disabled={props.openDeadline} value={props.deadline} onChange={(event) => props.onDeadline(event.target.value)} />
      <label className="inputLabel">Posting Dates *</label>
      <label className="caption"><input type="checkbox" checked={props.openPosting} onChange={(event) => props.onOpenPosting(event.target.checked)} /> Open / No End Date</label>
      <div className="flex flex-wrap gap-4">
        <input type="date" disabled={props.openPosting} value={props.dateFrom} onChange={(event) => props.onDateFrom(event.target.value)} />
        <input type="date" disabled={props.openPosting} value={props.dateTo} onChange={(event) => props.onDateTo(event.target.value)} />
      </div>
      <label className="inputLabel">Secondment Title *</label>
      <input type="text" maxLength={200} placeholder="Title" value={props.title} onChange={(event) => props.onTitle(event.target.value)} />
      <label className="inputLabel">Secondment Duration *</label>
      <select className="border border-gray-300 p-2" value={props.duration || ""} onChange={(event) => props.onDuration(Number(event.target.value))}>
        <option value="">Duration</option>
        {DURATIONS.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
      </select>
    </div>
  );
}

interface ChoiceProps {
  label: string;
  options: ManageSecondmentOption[];
  selected: number[];
  allSelected: boolean;
  showAll: boolean;
  onToggle: (id: number, checked: boolean) => void;
  onAll: (checked: boolean) => void;
}

export function SecondmentChoices(props: ChoiceProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const hidden = props.options.length > 20 && !expanded;
  const visible = hidden ? props.options.slice(0, 20) : props.options;
  return (
    <div>
      <label className="inputLabel">{props.label} *</label>
      <ul className="choice-list">
        {props.showAll ? (
          <li><label className="caption"><input type="checkbox" checked={props.allSelected} onChange={(event) => props.onAll(event.target.checked)} /> All</label></li>
        ) : null}
        {visible.map((row) => (
          <li key={row.id}>
            <label className="caption"><input type="checkbox" checked={props.selected.includes(row.id)} onChange={(event) => props.onToggle(row.id, event.target.checked)} /> {row.name}</label>
          </li>
        ))}
      </ul>
      {props.options.length > 20 ? (
        <button type="button" className="text-primary" onClick={() => setExpanded(!expanded)}>{expanded ? "View Less" : "View More"}</button>
      ) : null}
    </div>
  );
}

interface NarrativeProps {
  overview: string;
  description: string;
  noRequirements: boolean;
  requirements: string;
  additionalInfo: string;
  onOverview: (value: string) => void;
  onDescription: (value: string) => void;
  onNoRequirements: (value: boolean) => void;
  onRequirements: (value: string) => void;
  onAdditional: (value: string) => void;
}

export function SecondmentNarrative(props: NarrativeProps): ReactElement {
  return (
    <div className="stack">
      <label className="inputLabel">Firm Secondment Program Overview Description</label>
      <textarea value={props.overview} onChange={(event) => props.onOverview(event.target.value)} />
      <label className="inputLabel">Position Description *</label>
      <textarea value={props.description} maxLength={5000} onChange={(event) => props.onDescription(event.target.value)} />
      <label className="inputLabel">Position Requirements *</label>
      <label className="caption"><input type="checkbox" checked={props.noRequirements} onChange={(event) => props.onNoRequirements(event.target.checked)} /> No Requirements Specified</label>
      <textarea disabled={props.noRequirements} maxLength={2000} value={props.noRequirements ? "" : props.requirements} onChange={(event) => props.onRequirements(event.target.value)} />
      <label className="inputLabel">Additional Information</label>
      <textarea value={props.additionalInfo} maxLength={2000} onChange={(event) => props.onAdditional(event.target.value)} />
    </div>
  );
}
